import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  enableProdMode,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarConfig,
} from '@angular/material/snack-bar';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import {
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  Translation,
  TranslocoService,
  provideTransloco,
} from '@jsverse/transloco';

import { PureAbility, createMongoAbility } from '@casl/ability';
import {
  provideHttpCache,
  withHttpCacheInterceptor,
  withLocalStorage,
  withSessionStorage,
} from '@ngneat/cashew';
import { environment } from 'environments/environment';
import { provideQuillConfig } from 'ngx-quill';
import { Observable, catchError, forkJoin, lastValueFrom, of, tap } from 'rxjs';
import { routes } from './app.routes';
import { SelectivePreloadingStrategy } from './core/preloading/selective-preloading-strategy';
import { AuthService } from './core/services/auth/auth.service';
import {
  BOOT_TRANSLATION_BUNDLES,
  applyAliasFromTranslation,
} from './core/transloco/boot-translations';
import {
  TranslocoHttpLoader,
  TranslocoPageRouteSyncService,
} from './core/transloco/transloco-loader';
import { SNACKBAR_POSITION } from './services/snackbar/snackbar.service';
import {
  HttpErrorInterceptor,
  JwtInterceptor,
  TimezoneInterceptor,
} from './shared/interceptors';
import { BASE_PATH, Configuration } from './shared/openapi';

if (environment.production) {
  enableProdMode();
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimezoneInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: PureAbility,
      useValue: createMongoAbility(),
    },
    provideHttpCache(withLocalStorage(), withSessionStorage()),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withInterceptors([withHttpCacheInterceptor()])
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(SelectivePreloadingStrategy)
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker,
    }),
    {
      provide: MAT_TABS_CONFIG,
      useValue: { animationDuration: '0ms' },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        ...SNACKBAR_POSITION,
        duration: environment.snackbarDefaultDuration,
      } satisfies MatSnackBarConfig,
    },
    provideTransloco({
      config: {
        availableLangs: ['en', 'ro'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          logMissingKey: false,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(translocoPreloadFactory),
    provideAppInitializer(translocoPageRouteSyncFactory),
    provideAppInitializer(() => inject(AuthService).restoreSession()),
    {
      provide: BASE_PATH,
      useValue: environment.apiBaseUrl,
    },
    {
      provide: Configuration,
      useValue: new Configuration({
        basePath: environment.apiBaseUrl,
        withCredentials: true,
      }),
    },
    provideQuillConfig({
      theme: 'snow',
    }),
  ],
};

function translocoPreloadFactory(): Promise<unknown> {
  const translocoService = inject(TranslocoService);
  const pageI18n = inject(TranslocoPageRouteSyncService);
  const langs = ['en', 'ro'] as const;

  const safeLoad = (
    path: string,
    lang: string,
    aliasPath?: string
  ): Observable<Translation | null> =>
    translocoService.load(path).pipe(
      tap(translation => {
        if (!aliasPath) return;
        applyAliasFromTranslation(
          translocoService,
          translation,
          lang,
          aliasPath
        );
      }),
      catchError(() => of(null))
    );

  const loads = langs.flatMap(lang => [
    safeLoad(lang, lang),
    ...BOOT_TRANSLATION_BUNDLES.map(bundle =>
      safeLoad(`${bundle.path}/${lang}`, lang, bundle.alias)
    ),
  ]);
  // Shell UI (navbar, sidenav, dynamic-fields, …) for both langs before first paint.
  return lastValueFrom(
    forkJoin([...loads, pageI18n.preloadShellLanguages(langs)])
  );
}

function translocoPageRouteSyncFactory(): void {
  const syncService = inject(TranslocoPageRouteSyncService);
  syncService.init();
}
